/**
 * Shared user avatar dropdown used in the main AppShell header and PublicNav.
 *
 * Leading portal-switch items differ by surface (Admin Portal vs Go to Overview);
 * My Account and Logout stay shared.
 */

import type { ReactNode } from 'react';
import {
  Avatar,
  Group,
  Menu,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconLogout, IconUser } from '@tabler/icons-react';
import { brandColors } from '../../config/theme';
import { APP_ROUTES } from '../../constants/routes';

/** Minimal user fields needed for the avatar target. */
export interface UserAccountMenuUser {
  name?: string | null;
  email?: string | null;
  givenName?: string | null;
  surname?: string | null;
  profilePictureUrl?: string | null;
}

export interface UserAccountMenuProps {
  /** Current user for avatar initials / photo. */
  user: UserAccountMenuUser | null | undefined;
  /** Navigate callback (React Router navigate or equivalent). */
  navigate: (path: string) => void;
  /** Logout handler from AuthContext. */
  logout: () => void | Promise<void>;
  /**
   * Optional portal-switch / primary items rendered above My Account
   * (e.g. Go to Overview, Admin Portal). Caller owns labels and navigation.
   */
  leadingItems?: ReactNode;
  /** Show the display name next to the avatar from the `sm` breakpoint up. */
  showNameFrom?: 'xs' | 'sm' | 'md';
  /** Optional test id on the menu target button. */
  targetTestId?: string;
}

/**
 * Resolves a display name for the avatar label and initials.
 */
function resolveDisplayName(user: UserAccountMenuUser | null | undefined): string {
  if (!user) {
    return '';
  }

  const composed = `${user.givenName || ''} ${user.surname || ''}`.trim();
  return user.name || composed || user.email || '';
}

/**
 * Avatar + dropdown with shared My Account / Logout actions.
 */
export function UserAccountMenu({
  user,
  navigate,
  logout,
  leadingItems,
  showNameFrom = 'sm',
  targetTestId = 'user-account-menu-target',
}: UserAccountMenuProps) {
  const displayName = resolveDisplayName(user);

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <UnstyledButton data-testid={targetTestId}>
          <Group gap="xs" wrap="nowrap">
            <Avatar
              src={user?.profilePictureUrl || undefined}
              name={displayName || user?.email || undefined}
              color="initials"
              size="sm"
              radius="xl"
            />
            <Text size="sm" visibleFrom={showNameFrom} lineClamp={1}>
              {displayName || user?.email}
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {leadingItems}
        <Menu.Item
          leftSection={<IconUser size={14} />}
          onClick={() => navigate(APP_ROUTES.account)}
        >
          My Account
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconLogout size={14} />}
          color={brandColors.danger}
          onClick={() => {
            void logout();
          }}
        >
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
